import { FolderIcon, Paragraph, Tooltip, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { type GetWebcastSummaryManagerQuery, RecordingUploadStatus } from '../../../../../../generated/graphql-manager';
import { getWebcastTitle } from '../../../../../hooks/use-get-webcast-name';
import { DownloadFile } from '../download-file/download-file';

import styles from './event-summary-recording.module.css';

type EventSummaryRecordingProps = {
  webcast: GetWebcastSummaryManagerQuery['webcast'];
  language: string;
  multipleLanguages?: boolean;
};

export function EventSummaryRecording({ webcast, language, multipleLanguages }: EventSummaryRecordingProps) {
  const { t } = useTranslation();

  const recordings = webcast?.streaming?.recordings;
  const contents = webcast?.contents || [];

  const videoManager = webcast?.recordingSettings?.videoManager;
  const uploadChannels = videoManager?.channels;
  const uploadToVmProEnabled = videoManager?.enabled && uploadChannels && uploadChannels.length > 0;

  const recording = recordings?.find((rec) => rec.metadata.language === language);
  const recordingStatus = recording?.status;
  const downloadReady = recordingStatus === RecordingUploadStatus.READY_FOR_DOWNLOAD;
  const recordingNotReady = recordingStatus === RecordingUploadStatus.NOT_READY;
  const processingFailed = recordingStatus === RecordingUploadStatus.FAILED;

  const title = getWebcastTitle(language, contents) || '';
  const downloadName = `${title.split(' ').join('')}${multipleLanguages ? `-${language}` : ''}.mp4`;

  const tooltipText = processingFailed
    ? t('manager.eventSummary.recordingFailed')
    : recordingNotReady
      ? t('manager.eventSummary.recordingNotAvailable')
      : '';

  return (
    <div key={language} className={styles.container}>
      <div className={styles.eventRecordingContent}>
        <Tooltip label={title}>
          <Paragraph data-testid={`recording-title-${language}`} className={styles.textEllipsis}>
            {title}
          </Paragraph>
        </Tooltip>

        {uploadToVmProEnabled &&
          uploadChannels?.map((channel) => (
            <div key={channel.name} className={styles.uploadPathWrapper}>
              <Tooltip label={t('manager.eventSummary.recording.recordingPathHint')}>
                <FolderIcon />
              </Tooltip>
              <Tooltip label={channel.name}>
                <Paragraph
                  data-testid={`recording-channel-${channel.name}`}
                  className={classNames(styles.uploadPath, styles.textEllipsis)}
                >
                  {channel.name}
                </Paragraph>
              </Tooltip>
            </div>
          ))}
      </div>

      <div>
        <DownloadFile
          label={t('manager.eventSummary.recording.downloadButton')}
          webcastId={webcast.id}
          downloadLink={recording?.downloadLink}
          fileName={downloadName}
          disabled={!downloadReady}
          tooltipText={tooltipText}
          alwaysShowTooltip={processingFailed}
          buttonTestId={`event-recording-download-button-${language}`}
          tooltipTestId={`event-recording-download-tooltip-${language}`}
        />
      </div>
    </div>
  );
}
